'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { GAMES_JOBS_TENANT } from '../infrastructure/mazeGenerationQueue';
import {
  reserveMazeImage,
  refundMazeImage,
  InsufficientBalanceError,
  mazePricing,
} from '../infrastructure/mazeBilling';
import type { GameDifficulty } from '../types';

const DEFAULT_CANVAS_SIZE = 800;

interface ReEnqueueResult {
  jobId: string;
  mazeId: string;
}

/**
 * Re-enqueue an image maze at a different difficulty from an already-uploaded
 * source image key. PAID like a fresh image maze — reserve the price before
 * enqueue, refund on failure, settle on completion (see readMazeResult/consumer).
 * SSRF-guarded: the worker will fetch the key, so it must point at our own storage.
 */
export async function reEnqueueImageMaze(
  imageUrl: string,
  difficulty: GameDifficulty,
): Promise<ActionResult<ReEnqueueResult>> {
  try {
    const user = await SessionService.getUser();
    const ownerId = user?.identityId ?? null;
    if (!ownerId) return { success: false, error: 'Not authenticated' };

    if (!imageUrl) {
      return { success: false, error: 'Image URL is required' };
    }

    const accountId = user?.accountId ?? null;
    if (!accountId) {
      return { success: false, error: 'Sign in to generate image mazes.', code: 'SIGN_IN_REQUIRED' };
    }

    const { isOwnStorageUrl } = await import('@/contexts/media/actions/blobOwnership');
    if (!isOwnStorageUrl(imageUrl)) {
      return { success: false, error: 'Invalid image URL' };
    }

    const mazeId = crypto.randomUUID();
    const blobPath = `${ownerId}/${mazeId}/geometry.json`;

    try {
      await reserveMazeImage(accountId, mazeId, (await mazePricing()).imagePriceTokens);
    } catch (e) {
      if (e instanceof InsufficientBalanceError) {
        return {
          success: false,
          error: 'Insufficient token balance. Please top up to generate an image maze.',
          code: 'INSUFFICIENT_BALANCE',
        };
      }
      throw e;
    }

    try {
      const { getMazeGenerationQueue } = await import('../infrastructure/mazeGenerationQueue');

      const jobId = await getMazeGenerationQueue().enqueue(
        {
          type: 'image-maze',
          familyId: ownerId,
          mazeId,
          imageUrl,
          difficulty,
          blobPath,
          canvasWidth: DEFAULT_CANVAS_SIZE,
          canvasHeight: DEFAULT_CANVAS_SIZE,
        },
        { tenant: GAMES_JOBS_TENANT },
      );

      return { success: true, data: { jobId, mazeId } };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      await refundMazeImage(mazeId, `Image maze re-enqueue failed: ${msg}`).catch(() => {});
      throw e;
    }
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('reEnqueueImageMaze', e) };
  }
}
